import React from 'react';
import Drawer, { FooterSpacer } from '../../Drawer';
import Button from '../../Button';
import Field from '../../Field';
import { SWATCHES } from '../../../domain/categories';
import {
  TAG_KINDS, TAG_KIND_IDS, TAG_KIND_LABELS, TAGS_WORDING as W, tagFormTitle, tagCanSave, tagPickerRows,
  tagSelectedLabel,
} from '../../../domain/tags';
import s from './tags.module.css';

// Add Tag / Edit Tag, as one form.
//
// MMS's form:
//
//   Name          required
//   Tag Type      a dropdown, reading `Subject` for an instrument tag. Present
//                 on Edit and not on Add, because Add reached the form through
//                 a chooser that had just asked the same question.
//   Color         a swatch dropdown defaulting to `Beach`
//   Students      a multi-select with Quick Select, Clear Selection and a
//                 "Show inactive students in this list" checkbox
//   Description   free text, optional
//
// Buttons: `Save & Duplicate`, `Cancel`, `Save`.
//
// One form for both surfaces (#98). Tag Type is shown on both, where MMS
// shows it on Edit only: on Add it opens on what the chooser set, and a
// teacher who picked the wrong door can correct it without saving first.
//
// Not here (#138): Quick Select, whose menu is unrecorded, and Save &
// Duplicate, whose result is unrecorded — a duplicate is a second record of
// the same name, which `tagErrors` refuses.

export default function TagFormDrawer({
  open,
  draft,
  onChange,
  onSave,
  onClose,
  students = [],
  nameOf = (st) => st?.id,
  errors = {},
  busy = false,
}) {
  if (!open || !draft) return null;

  const set = (patch) => onChange({ ...draft, ...patch });
  const selected = new Set(draft.studentIds || []);
  const editing = Boolean(draft.id);

  // MMS's checkbox is "Show inactive students in this list", so the list is
  // active-only until it is ticked. It governs who can be added, not who is a
  // member: a student who goes inactive stays on every tag, and an inactive
  // member is shown whatever the checkbox says, so it can be unticked and the
  // grid's Students column agrees with this form. `tagPickerRows`
  // (src/domain/tags.js) is that rule, and which rows say "Inactive".
  const visible = tagPickerRows(students, { selected: [...selected], showInactive: draft.showInactive });

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    set({ studentIds: [...next] });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={tagFormTitle(draft.kind, editing)}
      width={560}
      footer={(
        <>
          <FooterSpacer />
          <Button variant="secondary" onClick={onClose}>{W.cancel}</Button>
          <Button onClick={onSave} loading={busy} disabled={!tagCanSave(draft.label)}>
            {W.save}
          </Button>
        </>
      )}
    >
      {/* `errors` is what the last Save refused, keyed by field (`tagErrors`
          in domain/tags.js). Empty until the first submit: the refusal arrives
          on submit, not while the teacher is typing (#116, #129). */}
      <Field label={W.fieldName} htmlFor="tag-name" error={errors.label} required>
        <input
          id="tag-name"
          className={s.input}
          value={draft.label || ''}
          disabled={busy}
          data-error={errors.label ? 'true' : undefined}
          onChange={(e) => set({ label: e.target.value })}
        />
      </Field>

      <Field
        label={W.fieldKind}
        htmlFor="tag-kind"
        hint={W.fieldKindHint}
      >
        <select
          id="tag-kind"
          className={s.select}
          value={draft.kind || TAG_KINDS.instrument}
          disabled={busy}
          onChange={(e) => set({ kind: e.target.value })}
        >
          {TAG_KIND_IDS.map((kind) => (
            <option key={kind} value={kind}>{TAG_KIND_LABELS[kind]}</option>
          ))}
        </select>
      </Field>

      <Field label={W.fieldColor}>
        <div className={s.swatches}>
          {SWATCHES.map((sw) => (
            <button
              key={sw}
              type="button"
              aria-label={`Colour ${sw}`}
              aria-pressed={draft.color === sw}
              className={draft.color === sw ? s.swatchButtonOn : s.swatchButton}
              style={{ background: sw }}
              disabled={busy}
              onClick={() => set({ color: sw })}
            />
          ))}
        </div>
      </Field>

      {/* Membership is edited here: a teacher opens "Piano" and ticks who plays
          it. Nothing on the student is written, and a rename carries every tick
          because they are stored on this document. */}
      <Field label={W.fieldStudents}>
        <div className={s.pickerHead}>
          <span className={s.pickerCount}>
            {tagSelectedLabel(selected.size)}
          </span>
          <span className={s.spacer} />
          <button
            type="button"
            className={s.plain}
            disabled={busy || selected.size === 0}
            onClick={() => set({ studentIds: [] })}
          >
            {W.clearSelection}
          </button>
        </div>

        <div className={s.picker}>
          {visible.length === 0 ? (
            <div className={s.pickerEmpty}>{W.nobodyToShow}</div>
          ) : visible.map(({ student: st, inactive }) => {
            return (
              <label key={st.id} className={s.pickerRow}>
                <input
                  type="checkbox"
                  checked={selected.has(st.id)}
                  disabled={busy}
                  onChange={() => toggle(st.id)}
                />
                <span>{nameOf(st)}</span>
                {inactive ? <span className={s.inactiveMark}>{W.inactiveMark}</span> : null}
              </label>
            );
          })}
        </div>

        <label className={s.checkRow}>
          <input
            type="checkbox"
            checked={Boolean(draft.showInactive)}
            disabled={busy}
            onChange={(e) => set({ showInactive: e.target.checked })}
          />
          <span>{W.showInactive}</span>
        </label>
      </Field>

      <Field label={W.fieldDescription} htmlFor="tag-description">
        <textarea
          id="tag-description"
          className={s.textarea}
          value={draft.description || ''}
          disabled={busy}
          onChange={(e) => set({ description: e.target.value })}
        />
      </Field>
    </Drawer>
  );
}
